<script setup>
// FLOW-05 (correção de 14/10): a escolha numa lista dentro do editor do flow (o tipo e o formato de
// um parâmetro, a operação do `aggregate`, as tentativas do `retry`, a opção de um `enum` nos dados
// do teste). Era o `<select>` nativo do `components-next`, cuja lista aberta é a do sistema, sem o
// desenho do OS; agora é o `FilterSelect`, com a lista desenhada e o teclado dele (setas, Home/End,
// Enter, Esc e Tab). O gatilho tem cara de campo: a altura (32px), o fundo e o contorno dos inputs
// da caixa do passo (o `Input` `sm`), com a seta de abrir à direita.
//
// `class` e `style` ficam na caixa (quem ocupa o espaço, como `w-36`); o resto dos atributos (id,
// aria-label, aria-describedby, data-*) vai ao gatilho, que é o que o leitor de tela anuncia, o que
// o `<label for>` aponta e o que o teste procura. O gatilho diz o valor escolhido em `data-valor`,
// como o `.value` do `<select>` dizia. A lista abre da largura do gatilho (`match-trigger-width`), como
// a do `<select>`: a opção longa não quebra em várias linhas com espaço sobrando ao lado.
//
// 16/10 (o agente do teste): `busca` põe a busca do `FilterSelect` no alto da lista aberta, para a
// lista longa; `textoDaBusca` é o que ela diz quando vazia.
//
// FLOW-13 (Task 5.2, o segredo da Autenticação): `icone` vai antes do valor no gatilho (o cadeado do
// segredo), `mono` escreve o valor em fonte mono (um nome do cofre), e `textoVazio` é o que o
// gatilho diz quando nada foi escolhido ("Escolha um segredo").
//
// FLOW-12 (Gate 4, 2ª rodada): `sair` avisa quando o foco sai da escolha inteira (o gatilho, a lista
// aberta e a busca dela), como o `blur` de um campo de texto: os dados do teste conferem a regra do
// param ao sair do campo, e a escolha vazia saía sem dizer "Preencha `canal`". Abrir a lista não é
// sair: o foco vai do gatilho para a lista, dentro da escolha.
import { computed, useAttrs } from 'vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';

const props = defineProps({
  // [{ value, label }]
  options: { type: Array, required: true },
  disabled: { type: Boolean, default: false },
  // O valor tem um problema (o contorno rubi, como o do `Input`); a frase fica com quem o usa.
  erro: { type: Boolean, default: false },
  busca: { type: Boolean, default: false },
  textoDaBusca: { type: String, default: '' },
  icone: { type: String, default: '' },
  mono: { type: Boolean, default: false },
  textoVazio: { type: String, default: '' },
});

const emit = defineEmits(['sair']);

defineOptions({ inheritAttrs: false });

const valor = defineModel({ type: [String, Number], default: '' });

function aoPerderOFoco(evento) {
  if (evento.currentTarget?.contains(evento.relatedTarget)) return;
  emit('sair');
}

const attrs = useAttrs();
// `mono` vale também para as opções da lista aberta, que o `FilterSelect` desenha, menos a de ação
// (`--acao`, o "Novo segredo" do fim da lista), que é texto.
const daCaixa = computed(() => ({
  class: [
    attrs.class,
    props.mono &&
      '[&_[role=option]]:font-mono [&_[role=option][data-value=--acao]]:font-sans',
  ],
  style: attrs.style,
}));
const doGatilho = computed(() =>
  Object.fromEntries(
    Object.entries(attrs).filter(
      ([chave]) => !['class', 'style'].includes(chave)
    )
  )
);

const rotulo = computed(
  () => props.options.find(opcao => opcao.value === valor.value)?.label ?? ''
);

const contorno = aberto => {
  if (props.erro) return 'outline-n-ruby-8 hover:enabled:outline-n-ruby-9';
  if (aberto) return 'outline-n-brand';
  return 'outline-n-weak hover:enabled:outline-n-slate-6 focus-visible:outline-n-brand';
};
</script>

<template>
  <FilterSelect
    v-model="valor"
    :options="options"
    match-trigger-width
    :searchable="busca"
    :search-placeholder="textoDaBusca"
    v-bind="daCaixa"
    @focusout="aoPerderOFoco"
  >
    <template #trigger="{ toggle, isOpen, triggerAttrs }">
      <button
        type="button"
        v-bind="{ ...triggerAttrs, ...doGatilho }"
        :disabled="disabled"
        :data-valor="valor"
        class="flex items-center justify-between w-full h-8 gap-2 px-3 py-0 m-0 text-sm text-left border-0 rounded-lg reset-base bg-n-alpha-black2 text-n-slate-12 outline outline-1 -outline-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
        :class="contorno(isOpen)"
        @click="toggle"
      >
        <span class="flex items-center flex-1 min-w-0 gap-2">
          <span
            v-if="icone"
            class="size-3.5 shrink-0 text-n-slate-11"
            :class="icone"
            aria-hidden="true"
          />
          <span
            v-if="rotulo"
            class="min-w-0 truncate"
            :class="{ 'font-mono': mono }"
          >
            {{ rotulo }}
          </span>
          <span v-else class="min-w-0 truncate text-n-slate-10">
            {{ textoVazio }}
          </span>
        </span>
        <span
          class="i-lucide-chevron-down size-4 shrink-0 text-n-slate-11"
          aria-hidden="true"
        />
      </button>
    </template>
  </FilterSelect>
</template>
